/**
 * Intelligente Zusammenführung (Aufgabenverwaltung 2.0): ZEO (Qwen/Mittwald) analysiert die vollständigen Originaldaten und schlägt vor;
 * Titel, Beschreibung, Hauptaufgabe und Auswahl sind bearbeitbar. Gespeichert wird erst nach Bestätigung – über die bestehende
 * Paket-A-Funktion (Unteraufgaben). Nichts wird gelöscht oder erledigt; Fristen, Argus, Dokumente und Verlauf bleiben an jeder Aufgabe.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, Layers, Loader2, FileText, Clock } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { betreuungAPI } from '@/api/betreuungAPI';
import { toast } from 'sonner';

function Liste({ titel, eintraege, farbe = 'text-slate-700' }) {
  if (!eintraege?.length) return null;
  return (
    <div>
      <p className="text-xs font-semibold text-slate-500 uppercase">{titel}</p>
      <ul className={`list-disc ml-5 text-sm ${farbe}`}>{eintraege.map((e, i) => <li key={i}>{e}</li>)}</ul>
    </div>
  );
}

export default function ZusammenfuehrenDialog({ open, ids, onClose, onDone }) {
  const [daten, setDaten] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [haupt, setHaupt] = useState(null);
  const [unter, setUnter] = useState(new Set());
  const [umhaengen, setUmhaengen] = useState(new Set());
  const [titel, setTitel] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const [laeuft, setLaeuft] = useState(false);

  const laden = () => {
    setDaten(null); setFehler(null);
    betreuungAPI.zusammenfuehrenVorschlag(ids).then(d => {
      setDaten(d);
      setHaupt(d.vorschlag.haupt_aufgabe_id);
      setUnter(new Set(d.vorschlag.unteraufgaben_ids));
      setUmhaengen(new Set());
      setTitel(d.vorschlag.titel);
      setBeschreibung(d.vorschlag.beschreibung);
    }).catch(e => setFehler(e.message));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) laden(); }, [open, ids]);

  const nr = (id) => daten?.aufgaben.find(a => a.id === id)?.nr;
  const fremdEltern = (a) => a.eltern && !a.eltern.in_auswahl && a.eltern.id !== haupt;
  const waehleHaupt = (id) => {
    setHaupt(id);
    setUnter(prev => { const n = new Set(prev); n.delete(id); const alt = daten.aufgaben.find(a => a.id === haupt); if (alt?.kann_unteraufgabe) n.add(haupt); return n; });
  };
  const toggle = (setter, id) => setter(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const offeneUmhaengung = daten ? daten.aufgaben.filter(a => unter.has(a.id) && fremdEltern(a) && !umhaengen.has(a.id)) : [];

  const bestaetigen = async () => {
    setLaeuft(true);
    try {
      const r = await betreuungAPI.zusammenfuehren({
        haupt_aufgabe_id: haupt, unteraufgaben_ids: [...unter], titel, beschreibung,
        staende: Object.fromEntries(daten.aufgaben.map(a => [a.id, a.stand])), umhaengen_bestaetigt: [...umhaengen],
      });
      if (r.bereits_ausgefuehrt) toast.info('Diese Zusammenführung war bereits gespeichert – nichts doppelt angelegt.');
      else if (r.ok) toast.success(`${r.unteraufgaben} Aufgabe(n) zusammengeführt (zurückgelesen)`);
      else toast.error(r.fehler || 'Zusammenführung nur teilweise bestätigt – bitte prüfen.');
      onDone?.(r);
    } catch (e) {
      setFehler(e.message);
    } finally {
      setLaeuft(false);
    }
  };

  const vs = daten?.vorschlag;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="zusammenfuehren-dialog">
        <DialogHeader>
          <DialogTitle>Intelligent zusammenführen</DialogTitle>
          <DialogDescription>Vorschau – es wurde noch nichts gespeichert. Die übrigen Aufgaben werden Unteraufgaben der Hauptaufgabe; nichts wird gelöscht oder erledigt.</DialogDescription>
        </DialogHeader>
        {fehler && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="zusammenfuehren-fehler">
            {fehler} <Button variant="link" size="sm" onClick={laden}>Vorschau neu laden</Button>
          </div>
        )}
        {!daten && !fehler && (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-slate-500"><Loader2 className="w-5 h-5 animate-spin" />ZEO analysiert die Aufgaben …</div>
        )}
        {daten && (
          <div className="space-y-4">
            {daten.ki.hinweis && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">{daten.ki.hinweis}</p>}
            {daten.hinweise.map(h => <p key={h} className="flex gap-2 text-xs text-slate-600"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-600" />{h}</p>)}
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-500 uppercase">Haupttitel</label>
              <Input value={titel} onChange={e => setTitel(e.target.value)} data-testid="zusammenfuehren-titel" />
              <label className="text-xs font-semibold text-slate-500 uppercase">Konsolidierte Beschreibung</label>
              <Textarea rows={7} value={beschreibung} onChange={e => setBeschreibung(e.target.value)} data-testid="zusammenfuehren-beschreibung" />
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <Liste titel="Offene Handlungsschritte" eintraege={vs.offene_schritte} />
              <Liste titel="Fachliche Widersprüche" eintraege={vs.widersprueche} farbe="text-amber-800" />
              <Liste titel="Offene Fragen" eintraege={vs.offene_fragen} />
              <Liste titel="Gehört vermutlich nicht dazu" eintraege={vs.nicht_zugehoerig.map(n => `Aufgabe ${n.nr}: ${n.grund}`)} farbe="text-amber-800" />
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Ausgewählte Aufgaben – Hauptaufgabe und Unteraufgaben wählen</p>
              <ul className="space-y-2">
                {daten.aufgaben.map(a => {
                  const info = vs.informationen.filter(i => i.nr === a.nr).map(i => i.inhalt);
                  const istHaupt = a.id === haupt;
                  return (
                    <li key={a.id} className={`border rounded p-2 text-sm ${istHaupt ? 'border-purple-400 bg-purple-50/50' : 'border-slate-200'}`} data-testid={`zf-aufgabe-${a.id}`}>
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="text-xs text-slate-400">#{a.nr}</span>
                        <label className={`flex items-center gap-1 text-xs ${a.kann_haupt ? '' : 'opacity-40'}`}>
                          <input type="radio" name="zf-haupt" checked={istHaupt} disabled={!a.kann_haupt} onChange={() => waehleHaupt(a.id)} data-testid={`zf-haupt-${a.id}`} />
                          Hauptaufgabe
                        </label>
                        <label className={`flex items-center gap-1 text-xs ${!istHaupt && a.kann_unteraufgabe ? '' : 'opacity-40'}`}>
                          <input type="checkbox" checked={unter.has(a.id)} disabled={istHaupt || !a.kann_unteraufgabe} onChange={() => toggle(setUnter, a.id)}
                            data-testid={`zf-unter-${a.id}`} />
                          wird Unteraufgabe
                        </label>
                        <span className="font-medium text-slate-800">{a.titel}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3">
                        <span>Status {a.status || 'offen'}</span>
                        {a.faellig && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(a.faellig).toLocaleDateString('de-DE')}</span>}
                        {a.frist && <span className="text-amber-800">{a.frist.text}</span>}
                        {a.unteraufgaben > 0 && <span>{a.unteraufgaben} eigene Unteraufgabe(n)</span>}
                        <span>{a.verlauf_anzahl} Verlauf · {a.notizen_anzahl} Notiz(en)</span>
                      </div>
                      {a.beschreibung && <p className="text-xs text-slate-600 mt-1 whitespace-pre-line">{a.beschreibung}</p>}
                      {a.dokumente.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">{a.dokumente.map(d => (
                          <span key={d.id} className="inline-flex items-center gap-1 text-xs bg-slate-100 rounded px-1.5 py-0.5" title={d.id}><FileText className="w-3 h-3" />{d.titel}</span>
                        ))}</div>
                      )}
                      {info.map((t, i) => <p key={i} className="text-xs text-purple-800 mt-1">ZEO: {t}</p>)}
                      {fremdEltern(a) && unter.has(a.id) && (
                        <label className="flex items-center gap-1 text-xs text-amber-800 mt-1">
                          <input type="checkbox" checked={umhaengen.has(a.id)} onChange={() => toggle(setUmhaengen, a.id)} data-testid={`zf-umhaengen-${a.id}`} />
                          ist bereits Unteraufgabe von „{a.eltern.titel || 'anderer Aufgabe'}“ – Umhängen ausdrücklich bestätigen
                        </label>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          {daten && (
            <Button onClick={bestaetigen} disabled={laeuft || !haupt || unter.size === 0 || !titel.trim() || offeneUmhaengung.length > 0}
              data-testid="zusammenfuehren-bestaetigen">
              {laeuft ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Layers className="w-4 h-4 mr-1" />}
              Zusammenführen bestätigen ({unter.size} unter #{nr(haupt)})
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
